import type { AddressFieldsResponse } from "@fluid-app/address-fields/types";
import {
  checkoutV202604,
  type operations,
} from "@fluid-app/checkout-api-client";
import { fetchApi } from "@/lib/api-helpers";
import { createPublicCheckoutApiClient } from "@/lib/checkout-api-client";
import { z } from "zod";
import { cartResponseSchema } from "@/networking/carts.api";

export const phoneSchema = (required: boolean, requiredMessage: string) => {
  const phoneString = z.preprocess(
    (value) => (value == null ? "" : value),
    z.string().trim(),
  );

  return required
    ? phoneString.pipe(z.string().min(1, requiredMessage))
    : z.preprocess(
        (value) => (value == null ? undefined : value),
        z.string().trim().optional(),
      );
};

const requiredStringSchema = (requiredMessage: string, minLength = 1) =>
  z.preprocess(
    (value) => (value == null ? "" : value),
    z.string().trim().min(minLength, requiredMessage),
  );

export const languageSchema = z.object({
  id: z.number(),
  iso: z.string(),
  name: z.string(),
});

const stateSchema = z.object({
  isoCode: z.string(),
  name: z.string(),
});

export type State = z.infer<typeof stateSchema>;

export const addressSchema = z.object({
  address: z.object({
    name: z.string(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    phone: z.string().optional(),
    country_code: z.string(),
  }),
  type: z.enum(["ship_to", "bill_to", "both"]),
  use_shipping_address_for_billing: z.boolean().optional(),
});

export type Address = z.infer<typeof addressSchema>;

const countrySchema = z.object({
  id: z.number(),
  iso: z.string(),
  name: z.string(),
  states: z.array(stateSchema).default([]),
});

export const countriesAndLanguagesSchema = z.object({
  languages: z.array(languageSchema),
  countries: z.array(countrySchema),
});

const cartInfoSchema = z.object({
  languages: z.array(languageSchema),
});

export const checkoutStoreCountriesSchema = z.object({
  company_countries: z.array(
    z.object({
      country: countrySchema,
    }),
  ),
});

const checkoutStoreCountriesInputSchema = z.object({
  cart_token: z.string().optional(),
  language_iso: z.string().optional(),
});

export type Country = z.infer<typeof countrySchema>;
export type CheckoutStoreCountries = z.infer<
  typeof checkoutStoreCountriesSchema
>;

export function countryStates(
  countries: Country[],
  countryCode: string,
): State[] | undefined {
  return countries.find((country) => country.iso === countryCode)?.states;
}

export async function fetchCheckoutStoreCountries({
  cartToken,
  languageIso,
  jwt,
}: {
  cartToken?: string;
  languageIso?: string;
  jwt?: string;
}) {
  const input = {
    ...(cartToken ? { cart_token: cartToken } : {}),
    ...(languageIso ? { language_iso: languageIso } : {}),
  };

  return fetchApi({
    endpoint: "/checkout/v2026-04/store/countries",
    method: "GET",
    input: Object.keys(input).length > 0 ? input : undefined,
    inputSchema: checkoutStoreCountriesInputSchema,
    outputSchema: checkoutStoreCountriesSchema,
    fetchOptions: jwt
      ? {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      : undefined,
  });
}

export async function fetchCountriesAndLanguages(
  cartToken: string,
  languageIso?: string,
) {
  const [cartInfo, storeCountries] = await Promise.all([
    fetchApi({
      endpoint: `/carts/${cartToken}/cart_info`,
      method: "GET",
      outputSchema: cartInfoSchema,
    }),
    fetchCheckoutStoreCountries({ cartToken, languageIso }),
  ]);

  return countriesAndLanguagesSchema.parse({
    languages: cartInfo.languages,
    countries: storeCountries.company_countries.map(
      (companyCountry) => companyCountry.country,
    ),
  });
}

export type CountriesAndLanguages = z.infer<typeof countriesAndLanguagesSchema>;

export async function updateCartCountry(
  cartToken: string,
  country: { country_code: string },
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/update_country`,
    method: "PATCH",
    input: country,
    outputSchema: cartResponseSchema,
  });
}

export async function updateAddress(address: Address, cartToken: string) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/address`,
    method: "POST",
    input: address,
    outputSchema: cartResponseSchema,
  });
}

export interface AddressTranslations {
  nameRequired?: string;
  /** @deprecated Use nameRequired instead */
  firstNameRequired?: string;
  addressLine1Required?: string;
  cityRequired?: string;
  stateRequired?: string;
  postalCodeRequired?: string;
  postalCodeMinCharacters?: string;
  countryRequired?: string;
  zipCodeInvalidForState?: string;
  phoneRequired?: string;
}

export const createBillingAddressSchema = (
  addressFields?: AddressField[],
  translations?: AddressTranslations,
) => {
  const req = (field: string) => {
    if (!addressFields || addressFields.length === 0) return true;
    return addressFields.find((f) => f.field === field)?.required ?? false;
  };

  return z.object({
    name: z.string().optional(),
    address1: req("address1")
      ? requiredStringSchema(
          translations?.addressLine1Required ?? "Address line 1 is required",
        )
      : z.string().optional(),
    address2: z.string().optional(),
    city: req("city")
      ? requiredStringSchema(translations?.cityRequired ?? "City is required")
      : z.string().optional(),
    state: req("state")
      ? requiredStringSchema(translations?.stateRequired ?? "State is required")
      : z.string().optional(),
    postal_code: req("postal_code")
      ? requiredStringSchema(
          translations?.postalCodeRequired ?? "Postal code is required",
        )
      : z.string().optional(),
    phone: phoneSchema(
      req("phone"),
      translations?.phoneRequired ?? "Enter a phone number for this address",
    ),
    country: requiredStringSchema(
      translations?.countryRequired ?? "Country is required",
    ),
  });
};

// default for backward compatibility
export const billingAddressSchema = createBillingAddressSchema();

export const dynamicAddressSchema = (
  addressFields: AddressField[],
  translations?: AddressTranslations,
) => {
  const req = (field: string) => {
    if (addressFields.length === 0) return true;
    return addressFields.find((f) => f.field === field)?.required ?? false;
  };

  return z.object({
    name:
      req("name") || req("first_name")
        ? requiredStringSchema(
            translations?.nameRequired ??
              translations?.firstNameRequired ??
              "Name is required",
          )
        : z.string().optional(),
    address1: req("address1")
      ? requiredStringSchema(
          translations?.addressLine1Required ?? "Address line 1 is required",
        )
      : z.string().optional(),
    address2: z.string().optional(),
    city: req("city")
      ? requiredStringSchema(translations?.cityRequired ?? "City is required")
      : z.string().optional(),
    state: req("state")
      ? requiredStringSchema(translations?.stateRequired ?? "State is required")
      : z.string().optional(),
    postal_code: req("postal_code")
      ? requiredStringSchema(
          translations?.postalCodeRequired ?? "Postal code is required",
        )
      : z.string().optional(),
    phone: phoneSchema(
      req("phone"),
      translations?.phoneRequired ?? "Enter a phone number for this address",
    ),
    country: requiredStringSchema(
      translations?.countryRequired ?? "Country is required",
    ),
  });
};

export const shippingAddressSchema = (
  addressFields: AddressField[],
  translations?: AddressTranslations,
) =>
  dynamicAddressSchema(addressFields, translations).extend({
    country_code: z.string(),
  });

export const createCustomerAddressSchema = (
  translations?: AddressTranslations,
) =>
  z.object({
    name: requiredStringSchema(
      translations?.nameRequired ??
        translations?.firstNameRequired ??
        "Name is required",
    ),
    address1: requiredStringSchema(
      translations?.addressLine1Required ?? "Address line 1 is required",
    ),
    address2: z.string().optional(),
    city: requiredStringSchema(
      translations?.cityRequired ?? "City is required",
    ),
    state: requiredStringSchema(
      translations?.stateRequired ?? "State is required",
    ),
    postal_code: requiredStringSchema(
      translations?.postalCodeMinCharacters ??
        "Postal code must be at least 4 characters",
      4,
    ),
    country: requiredStringSchema(
      translations?.countryRequired ?? "Country is required",
    ),
    phone: z.string().optional(),
    default: z.boolean().optional(),
  });

// default for backward compatibility
export const customerAddressSchema = createCustomerAddressSchema();

export type AddressFormData = z.infer<typeof customerAddressSchema>;

const customerMetaSchema = z.object({
  request_id: z.string(),
  timestamp: z.string(),
  pagination: z
    .object({
      current_page: z.number(),
      total_pages: z.number(),
      total_count: z.number(),
      per_page: z.number(),
    })
    .optional(),
});

const customerAddressElementSchema = z.object({
  id: z.number(),
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  country_code: z.string().nullable(),
  default: z.boolean(),
  name: z.string().nullable(),
  postal_code: z.string(),
  phone: z.string().nullable().optional(),
  state: z.string(),
  subdivision_code: z.string().nullable(),
});

const customerAddressResponseSchema = z.object({
  // /fluid_pay can serve customer-scoped data behind this legacy envelope.
  // In that mode the backend returns no shared FluidPayAccount wrapper.
  fluid_pay_account: z
    .object({
      id: z.number(),
      default_address_id: z.number().nullable(),
    })
    .nullable(),
  addresses: z.array(customerAddressElementSchema),
  meta: customerMetaSchema,
});

export type CustomerAddressResponse = z.infer<
  typeof customerAddressResponseSchema
>;

export type CustomerAddress = z.infer<typeof customerAddressElementSchema>;

const createAddressBodySchema = z.object({
  address: z.object({
    address1: z.string(),
    address2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    phone: z.string().optional(),
    country_code: z.string(),
    name: z.string(),
    default: z.boolean(),
  }),
});

export type CreateAddressBody = z.infer<typeof createAddressBodySchema>;

export const createCustomerAddressResponseSchema = z.object({
  address: z.object({
    id: z.number(),
    address1: z.string(),
    address2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    postal_code: z.string(),
    phone: z.string().nullable().optional(),
    country_code: z.string(),
    name: z.string().nullable(),
    default: z.boolean(),
  }),
  // /fluid_pay can serve customer-scoped data behind this legacy envelope.
  // In that mode the backend returns no shared FluidPayAccount wrapper.
  fluid_pay_account: z
    .object({
      id: z.number(),
    })
    .nullable(),
});

export type CreateCustomerAddressResponse = z.infer<
  typeof createCustomerAddressResponseSchema
>;

type StoreAddressFieldsResponse =
  operations["checkout_v2026_04_get_store_address_fields"]["responses"][200]["content"]["application/json"];

type StoreAddressFieldsQuery =
  operations["checkout_v2026_04_get_store_address_fields"]["parameters"]["query"];

export type AddressField =
  StoreAddressFieldsResponse["address_fields"]["fields"][number];

export async function fetchCustomerAddresses(jwt: string) {
  const query = new URLSearchParams({
    jwt,
    page: "1",
    per_page: "100",
  });
  return fetchApi({
    endpoint: `/fluid_pay/addresses?${query}`,
    method: "GET",
    outputSchema: customerAddressResponseSchema,
  });
}

export async function addAddressToCart(
  cartToken: string,
  type: string,
  jwt: string,
  address_id: string,
) {
  const query = new URLSearchParams({
    type,
    jwt,
    address_id,
  }).toString();
  return fetchApi({
    endpoint: `/carts/${cartToken}/address?${query}`,
    method: "POST",
    outputSchema: cartResponseSchema,
  });
}

export async function createCustomerAddress(
  jwt: string,
  body: CreateAddressBody,
) {
  const query = new URLSearchParams({
    jwt,
  });
  return fetchApi({
    endpoint: `/fluid_pay/create_address?${query}`,
    method: "POST",
    input: body,
    inputSchema: createAddressBodySchema,
    outputSchema: createCustomerAddressResponseSchema,
  });
}

export async function deleteCustomerAddress(address_id: string, jwt: string) {
  return fetchApi({
    endpoint: `/fluid_pay/delete_address/${address_id}?jwt=${jwt}`,
    method: "DELETE",
    outputSchema: z.object({}),
  });
}

export async function updateCustomerAddress(
  address_id: string,
  jwt: string,
  body: CreateAddressBody,
) {
  return fetchApi({
    endpoint: `/fluid_pay/update_address/${address_id}?jwt=${jwt}`,
    method: "PATCH",
    input: body,
    inputSchema: createAddressBodySchema,
    outputSchema: createCustomerAddressResponseSchema,
  });
}

export async function fetchAddressFields(
  countryCode: string,
  languageIso: string,
): Promise<AddressFieldsResponse> {
  const response =
    await checkoutV202604.checkout_v2026_04_get_store_address_fields(
      createPublicCheckoutApiClient(),
      countryCode,
      { language_iso: languageIso } satisfies StoreAddressFieldsQuery,
    );

  const meta = response.meta?.timestamp
    ? {
        request_id: response.meta.request_id ?? null,
        timestamp: response.meta.timestamp,
      }
    : undefined;

  return {
    address_fields: response.address_fields,
    ...(meta ? { meta } : {}),
  };
}

const applyAddressToSubscriptionsResponseSchema = z.object({
  affected_count: z.number(),
  status: z.number(),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

export type ApplyAddressToSubscriptionsResponse = z.infer<
  typeof applyAddressToSubscriptionsResponseSchema
>;

export type ApplyAddressToSubscriptionsBody =
  | { apply_to_all: true }
  | { subscription_ids: number[] };

export async function applyAddressToSubscriptions(
  address_id: number,
  jwt: string,
  body: ApplyAddressToSubscriptionsBody,
): Promise<ApplyAddressToSubscriptionsResponse> {
  return fetchApi({
    endpoint: `/checkout/v2026-04/customers/me/addresses/${address_id}/apply_to_subscriptions`,
    method: "POST",
    input: body,
    outputSchema: applyAddressToSubscriptionsResponseSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}
